import { captureException } from '@sentry/react';
import { doc, runTransaction } from 'firebase/firestore';
import { User } from '../../shared/types/types';
import Logger from '../../utils/analytics/Logger/Logger';
import { REJECTED } from '../../utils/enums';
import { firestore } from '../../utils/firebase';
import { mapDatabaseDataToUser } from '../../utils/user/UserDataUtils';
import { COLLECTIONS, USER_PATH } from '../FirebaseConstants';
import WorkspaceAPI from '../Workspaces/WorkspaceAPI';
import WorkspaceData from '../Workspaces/WorkspaceData';
import WorkspacesUtils from '../Workspaces/WorkspacesUtils';
import UserAPICore from './UserAPICore';

class UserWorkspacesAPI extends UserAPICore {
  static setupWorkspaceIfNotSetup = async (user: User) => {
    // 1. Check if user has selected a workspace
    // 2. If not, check if there is any workspaces the user is a member of
    // 3. If there is, select the first one
    // 4. If there is not, create a new workspace and select it

    //  1. Check if user has selected a workspace
    if (!user.data.hasOnboarded) return;
    if (user.userId.length === 0) return;
    if (user.workspaces.selectedWorkspaceId && user.workspaces.selectedWorkspaceId !== 'pending') {
      // console.log('User has selected a workspace');
      return;
    }
    console.log('%cS%c Trying to set workspace for user', 'color:cyan; font-size: 8px', '');

    // 2. If not, check if there is any workspaces the user is a member of
    await UserWorkspacesAPI.assignWorkspaceFromExistingWorkspacesOrCreateNew(user);
  }

  static assignWorkspaceFromExistingWorkspacesOrCreateNew = async (user: User) => {
    const myWorkspaces = await WorkspaceAPI.getWorkspaces(user.data.email);

    // 3. If there is, select the first one
    if (myWorkspaces.length > 0) {
      console.log('User is a member of at least one workspace');
      const firstWorkspace = myWorkspaces[0];
      await UserWorkspacesAPI.setMyWorkspaceId(user.userId, firstWorkspace.workspaceId);
      return;
    }

    // TODO: Bad code design.
    // 4. If there is not, create a new workspace and select it
    const result = await UserWorkspacesAPI
      .setMyWorkspaceIdToPendingIfNotSetTransaction(user.userId);
    if (!result) return; // Another transaction has already set the workspaceId to pending

    const newWorkspace = await UserWorkspacesAPI.createInitialPrivateWorkspace(user);
    await UserWorkspacesAPI.setMyWorkspaceId(user.userId, newWorkspace.workspaceId);
  };

  static setMyWorkspaceId = async (userId: string, workspaceId: string) => {
    if (!userId) {
      captureException(new Error('setMyWorkspaceId: userId is empty'));
      return REJECTED;
    }
    if (!workspaceId) {
      console.log('setMyWorkspaceId: workspaceId is empty', { userId, workspaceId });
      captureException(new Error('setMyWorkspaceId: workspaceId is empty'));
      return REJECTED;
    }
    console.log('setMyWorkspaceId', { userId, workspaceId });
    Logger.Workspaces.logSetMyWorkspaceId(userId, workspaceId);
    const updates = {
      [USER_PATH.workspaces.selectedWorkspaceId]: workspaceId,
    };
    return UserAPICore.updateUser(userId, updates, USER_PATH.workspaces.selectedWorkspaceId);
  };

  /**
   * We just want one function to create a new workspace, so we use a transaction
   */
  static setMyWorkspaceIdToPendingIfNotSetTransaction = async (userId: string) => {
    const updates = {
      [USER_PATH.workspaces.selectedWorkspaceId]: 'pending',
    };

    // const userRef = firestore().collection(COLLECTIONS.USERS).doc(userId);
    const userDocRef = doc(firestore, COLLECTIONS.USERS, userId);

    // return firestore().runTransaction((transaction) =>
    // transaction.get(userRef).then((userDoc) => {
    return runTransaction(firestore, async (transaction) => {
      const userDoc = await transaction.get(userDocRef);
      const userData = mapDatabaseDataToUser(userDoc.data(), userId);
      if (userData.workspaces.selectedWorkspaceId) {
        return false;
      }
      console.log('%cS%c Updating selected workspaceId to pending', 'color:cyan; font-size: 8px', '');
      transaction.update(userDocRef, updates);
      return true;
    })
      .then((result) => {
        console.log('%cS%c Return from transaction workspaceId', 'color:cyan; font-size: 8px', '', result);
        return result;
      });
  };

  /**
   * If we are deleting the workspace in which is also the selected workspace
   * for a user, we need to select a different workspace for the user.
   * If we set the selected workspace to be an empty string, we will in
   * `UserGeneralAPI.listenToMyData()` set the selected workspace to either be
   * one of the workspaces the user is a member of, or if none, create a new
   * workspace and select it.
   */
  static resetSelectedWorkspaceIdIfItsGoingToBeDeleted = async (
    user: User, workspaceIdToBeDeleted: string,
  ) => {
    if (workspaceIdToBeDeleted === user.workspaces.selectedWorkspaceId) {
      Logger.Workspaces.logResetSelectedWorkspaceId(user.userId);
      const updates = {
        [USER_PATH.workspaces.selectedWorkspaceId]: '',
      };
      return UserAPICore.updateUser(user.userId, updates, USER_PATH.workspaces.selectedWorkspaceId);
    }
    return 'rejected';
  };

  static createInitialPrivateWorkspace = async (user: User): Promise<WorkspaceData> => {
    console.log('createInitialPrivateWorkspace');
    Logger.Workspaces.logCreateInitialPrivateWorkspace(user.userId);
    const userName = user.data.firstName || user.data.name;
    const newWorkspaceName = `${userName}'s - Personal`;
    const newWorkspace = WorkspacesUtils.createNewWorkspace(newWorkspaceName, user.data.email);
    const workspaceId = await WorkspaceAPI.createWorkspace(newWorkspace, user.userId);
    const createdWorkspace = new WorkspaceData(workspaceId, newWorkspace);
    return createdWorkspace;
  };
}
export default UserWorkspacesAPI;
